// ============================================================================
// Base API Client for Patient Survey SPA
// ============================================================================

/**
 * Base API client with fetch wrapper and error handling.
 * Provides typed HTTP methods for consistent API communication.
 */

import type { ApiErrorResponse } from '@/types/api';
import { getEnv } from '@/lib/env';

// ============================================================================
// Configuration
// ============================================================================

/**
 * Base URL for API requests.
 * Uses VITE_API_URL environment variable or defaults to localhost.
 * At runtime, reads window.__ENV__.API_URL (K8s ConfigMap) before VITE_API_URL.
 */
const API_BASE_URL = getEnv('API_URL') || 'http://localhost:43011';

/**
 * Default timeout for API requests in milliseconds.
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * LocalStorage key for patient authentication token.
 */
const TOKEN_STORAGE_KEY = 'patient_token';

/**
 * LocalStorage key for patient refresh token.
 */
const REFRESH_TOKEN_STORAGE_KEY = 'patient_refresh_token';

// ============================================================================
// Token Management
// ============================================================================

/**
 * Get the current authentication token from storage.
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

/**
 * Set the authentication token in storage.
 */
export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

/**
 * Remove the authentication token from storage.
 */
export function clearAuthToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * Get the refresh token from storage.
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
}

/**
 * Set the refresh token in storage.
 */
export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
}

/**
 * Remove the refresh token from storage.
 */
export function clearRefreshToken(): void {
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
}

// ============================================================================
// Token Refresh Logic
// ============================================================================

/**
 * Flag to prevent multiple simultaneous refresh attempts.
 */
let isRefreshing = false;

/**
 * Queue of pending requests waiting for token refresh.
 */
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

/**
 * Process the queue of pending requests after token refresh.
 */
function processQueue(error: Error | null, token: string | null = null): void {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  failedQueue = [];
}

/**
 * Attempt to refresh the access token using the refresh token.
 * Handles race conditions by queuing simultaneous requests.
 *
 * @returns Promise resolving to the new access token
 * @throws {Error} When refresh fails or no refresh token is available
 */
async function handleTokenRefresh(): Promise<string> {
  console.log('[Token Refresh] Starting token refresh...');

  // If already refreshing, queue this request
  if (isRefreshing) {
    console.log('[Token Refresh] Already refreshing, queuing request');
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      console.error('[Token Refresh] No refresh token found in localStorage');
      throw new Error('No refresh token available');
    }

    console.log('[Token Refresh] Refresh token exists, calling API...');

    // Call refresh endpoint directly (avoid circular dependency)
    const response = await fetch(`${API_BASE_URL}/patient/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    const newAccessToken = data.accessToken || data.access_token;
    const newRefreshToken = data.refreshToken || data.refresh_token;

    console.log('[Token Refresh] Response received:', {
      hasAccessToken: !!newAccessToken,
      hasRefreshToken: !!newRefreshToken
    });

    if (!newAccessToken) {
      throw new Error('No access token in refresh response');
    }

    // Update stored tokens (both access and refresh)
    setAuthToken(newAccessToken);
    console.log('[Token Refresh] Stored new access token');

    if (newRefreshToken) {
      setRefreshToken(newRefreshToken);
      console.log('[Token Refresh] Stored new refresh token');
    } else {
      console.warn('[Token Refresh] No refresh token in response!');
    }

    // Process queued requests
    processQueue(null, newAccessToken);
    console.log('[Token Refresh] Success! Processed', failedQueue.length, 'queued requests');

    return newAccessToken;
  } catch (error) {
    // Clear tokens on refresh failure
    clearAuthToken();
    clearRefreshToken();

    // Reject queued requests
    processQueue(error instanceof Error ? error : new Error('Token refresh failed'), null);

    console.error('Token refresh failed:', error);

    // Only redirect if we're not already on the session page
    // This prevents infinite redirect loops
    const currentPath = window.location.pathname;
    const isAlreadyOnSessionPage = currentPath.startsWith('/s/') ||
                                   currentPath.startsWith('/org/') ||
                                   currentPath === '/';

    if (!isAlreadyOnSessionPage) {
      const lastSession = sessionStorage.getItem('last_survey_session');
      if (lastSession) {
        window.location.href = `/s/${lastSession}`;
      }
    }

    throw error;
  } finally {
    isRefreshing = false;
  }
}

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Custom error class for API errors.
 * Contains HTTP status code and structured error details.
 *
 * @remarks
 * - Extends Error for proper stack traces
 * - Includes status code for error handling
 * - Contains original API error response when available
 */
export class ApiError extends Error {
  /**
   * HTTP status code from the response
   */
  public readonly status: number;

  /**
   * Error type identifier from API (e.g., 'Bad Request', 'Not Found')
   */
  public readonly errorType?: string;

  /**
   * Original API error response, if available
   */
  public readonly response?: ApiErrorResponse;

  constructor(status: number, message: string, errorType?: string, response?: ApiErrorResponse) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorType = errorType;
    this.response = response;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    const ErrorWithCapture = Error as unknown as { captureStackTrace?: (target: object, constructor: object) => void };
    if (ErrorWithCapture.captureStackTrace) {
      ErrorWithCapture.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Check if this is a network error (status 0 or no response)
   */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /**
   * Check if this is a client error (4xx status)
   */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * Check if this is a server error (5xx status)
   */
  get isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * Check if this is a not found error (404)
   */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * Check if this is an unauthorized error (401)
   */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

// ============================================================================
// Request Options
// ============================================================================

/**
 * Extended options for API requests.
 */
export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  /**
   * Request body - will be JSON stringified
   */
  body?: unknown;

  /**
   * Request timeout in milliseconds
   * @default 30000
   */
  timeout?: number;

  /**
   * Whether to skip JSON parsing of response
   * @default false
   */
  skipJsonParse?: boolean;
}

// ============================================================================
// API Client Functions
// ============================================================================

/**
 * Creates an AbortController with a timeout.
 *
 * @param timeout - Timeout in milliseconds
 * @returns Object containing the controller and a cleanup function
 */
function createTimeoutController(timeout: number): {
  controller: AbortController;
  cleanup: () => void;
} {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return {
    controller,
    cleanup: () => clearTimeout(timeoutId),
  };
}

/**
 * Parses an error response from the API.
 *
 * @param response - The fetch Response object
 * @returns Parsed error details
 */
async function parseErrorResponse(response: Response): Promise<{
  message: string;
  errorType?: string;
  apiError?: ApiErrorResponse;
}> {
  try {
    const data = await response.json();

    // Check if it matches our ApiErrorResponse structure
    if (data && typeof data === 'object' && 'message' in data) {
      const apiError = data as ApiErrorResponse;
      // NestJS wraps error in message.message when using exception filters
      const rawMsg = apiError.message;
      const message = typeof rawMsg === 'string'
        ? rawMsg
        : (rawMsg && typeof rawMsg === 'object' && 'message' in (rawMsg as Record<string, unknown>))
          ? String((rawMsg as Record<string, unknown>).message)
          : String(rawMsg);
      return {
        message,
        errorType: apiError.error,
        apiError,
      };
    }

    // Try to extract a message from the response
    if (typeof data === 'string') {
      return { message: data };
    }

    return { message: response.statusText || 'Request failed' };
  } catch {
    // If JSON parsing fails, use status text
    return { message: response.statusText || 'Request failed' };
  }
}

/**
 * Makes a typed API request to the backend.
 *
 * @typeParam T - Expected response type
 * @param endpoint - API endpoint (relative to base URL, should start with /)
 * @param options - Request options including method, body, headers, etc.
 * @returns Promise resolving to the typed response data
 * @throws {ApiError} When the request fails or returns a non-2xx status
 *
 * @example
 * ```typescript
 * // GET request
 * const survey = await apiRequest<SurveyDto>('/surveys/123');
 *
 * // POST request with body
 * const response = await apiRequest<ResponseDto>('/responses', {
 *   method: 'POST',
 *   body: { surveyId: '123', answers: [] },
 * });
 * ```
 */
export async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {},
  isRetry = false
): Promise<T> {
  const {
    timeout = DEFAULT_TIMEOUT,
    skipJsonParse = false,
    body,
    headers: customHeaders,
    ...fetchOptions
  } = options;

  // Build request URL
  const url = `${API_BASE_URL}${endpoint}`;

  // Build headers with optional auth token
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...customHeaders,
  };

  // Create timeout controller
  const { controller, cleanup } = createTimeoutController(timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    // Handle non-2xx responses
    if (!response.ok) {
      // Handle 401 Unauthorized - attempt token refresh
      if (response.status === 401 && !isRetry) {
        cleanup(); // Clean up current timeout

        try {
          // Attempt to refresh the token
          await handleTokenRefresh();

          // Retry the request with new token (isRetry = true to prevent infinite loop)
          return await apiRequest<T>(endpoint, options, true);
        } catch (refreshError) {
          // Token refresh failed - let the error propagate
          // User will be redirected in handleTokenRefresh
          throw new ApiError(401, 'Session expired. Please log in again.', 'Unauthorized');
        }
      }

      const { message, errorType, apiError } = await parseErrorResponse(response);
      throw new ApiError(response.status, message, errorType, apiError);
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204 || skipJsonParse) {
      return undefined as unknown as T;
    }

    // Parse JSON response
    const data = await response.json();
    return data as T;
  } catch (error) {
    // Handle timeout/abort errors
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError(0, 'Request timeout', 'Timeout');
    }

    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof TypeError) {
      throw new ApiError(0, 'Network error - please check your connection', 'NetworkError');
    }

    // Handle unknown errors
    throw new ApiError(
      0,
      error instanceof Error ? error.message : 'An unexpected error occurred',
      'UnknownError'
    );
  } finally {
    cleanup();
  }
}

// ============================================================================
// Convenience Methods
// ============================================================================

/**
 * Makes a GET request to the API.
 *
 * @typeParam T - Expected response type
 * @param endpoint - API endpoint
 * @param options - Optional request options
 * @returns Promise resolving to the typed response data
 */
export async function get<T>(
  endpoint: string,
  options: Omit<ApiRequestOptions, 'method' | 'body'> = {}
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'GET' });
}

/**
 * Makes a POST request to the API.
 *
 * @typeParam T - Expected response type
 * @typeParam B - Request body type
 * @param endpoint - API endpoint
 * @param body - Request body (will be JSON stringified)
 * @param options - Optional request options
 * @returns Promise resolving to the typed response data
 */
export async function post<T, B = unknown>(
  endpoint: string,
  body: B,
  options: Omit<ApiRequestOptions, 'method' | 'body'> = {}
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'POST', body });
}

/**
 * Makes a PUT request to the API.
 *
 * @typeParam T - Expected response type
 * @typeParam B - Request body type
 * @param endpoint - API endpoint
 * @param body - Request body (will be JSON stringified)
 * @param options - Optional request options
 * @returns Promise resolving to the typed response data
 */
export async function put<T, B = unknown>(
  endpoint: string,
  body: B,
  options: Omit<ApiRequestOptions, 'method' | 'body'> = {}
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'PUT', body });
}

/**
 * Makes a PATCH request to the API.
 *
 * @typeParam T - Expected response type
 * @typeParam B - Request body type
 * @param endpoint - API endpoint
 * @param body - Request body (will be JSON stringified)
 * @param options - Optional request options
 * @returns Promise resolving to the typed response data
 */
export async function patch<T, B = unknown>(
  endpoint: string,
  body: B,
  options: Omit<ApiRequestOptions, 'method' | 'body'> = {}
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'PATCH', body });
}

/**
 * Makes a DELETE request to the API.
 *
 * @typeParam T - Expected response type
 * @param endpoint - API endpoint
 * @param options - Optional request options
 * @returns Promise resolving to the typed response data
 */
export async function del<T>(
  endpoint: string,
  options: Omit<ApiRequestOptions, 'method'> = {}
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'DELETE' });
}

// ============================================================================
// Exports
// ============================================================================

/**
 * API client object with all HTTP methods.
 * Alternative to importing individual functions.
 *
 * @example
 * ```typescript
 * import { api } from '@/services/api';
 *
 * const data = await api.get<MyType>('/endpoint');
 * const result = await api.post<ResultType>('/endpoint', { data: 'value' });
 * ```
 */
export const api = {
  request: apiRequest,
  get,
  post,
  put,
  patch,
  delete: del,
} as const;

export default api;
